import { Tooltip } from "@mui/material";
import React, { useEffect, useState } from "react";
import { FcDocument } from "react-icons/fc";
import { FormattedMessage, useIntl } from "react-intl";
import { useNavigate, useSearchParams } from "react-router-dom";
import { SortingRule } from "react-table";
import { Col, Row } from "reactstrap";
import ButtonComponent from "../../../components/ButtonComponent";
import InputComponent from "../../../components/InputComponent";
import TableComponent, {
  OperationsCell,
} from "../../../components/TableComponent";
import { useAvailableModulesContext } from "../../../context/AvailableModulesContext";
import Toast from "../../../helpers/Toast";
import { Utilities } from "../../../helpers/Utilities";
import { EquipmentDTO } from "../../../models/Equipment/EquipmentDTO";
import { Parameter } from "../../../models/Parameter";
import { EquipmentService } from "../../../services/EquipmentService";

export default function EquipmentIndexB() {
  const intl = useIntl();
  //Declaração de outros estados e variáveis importantes
  const navigate = useNavigate();
  const equipmentService = new EquipmentService();
  const pageSizes: number[] = [5, 15, 30];
  const [data, setData] = useState<EquipmentDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [pageCount, setPageCount] = useState(1);
  const [searchParams] = useSearchParams();

  //Vamos tentar obter a page e a pageSize pelos query parameters
  //Usamos useState em vez de simple var, para não estar sempre a obter o valor a cada re-render, desta forma o page e o pageSize são sempre o mesmo
  //porque nunca estamos a fazer setState à var
  const [page] = useState<number>(Utilities.LoadPageFromURLQuery("page"));
  const [pageSize] = useState<number>(
    Utilities.LoadPageSizeFromURLQuery(pageSizes)
  );

  //Search Parameters
  const [carPlate, setCarPlate] = useState<string>(Utilities.LoadParameterFromURLQuery("CarPlate", "string", ""));
  const [serialNumber, setSerialNumber] = useState<string>(Utilities.LoadParameterFromURLQuery("SerialNumber", "string", ""));
  const [brand, setBrand] = useState<string>(Utilities.LoadParameterFromURLQuery("Brand", "string", ""));
  const [model, setModel] = useState<string>(Utilities.LoadParameterFromURLQuery("Model", "string", ""));
  const [sku, setSku] = useState<string>(Utilities.LoadParameterFromURLQuery("Sku", "string", ""));

  const [codClient, setCodClient] = useState<string>(Utilities.LoadParameterFromURLQuery("CodClient", "string", ""));
  const [clientName, setClientName] = useState<string>(Utilities.LoadParameterFromURLQuery("ClientName", "string", ""));

  const [currentPage, setCurrentPage] = useState<number>();
  const [currentPageSize, setCurrentPageSize] = useState<number>();
  const [currentSorting, setCurrentSorting] = useState<SortingRule<object>[] | null>(null);
  const [goToPage, setGoToPage] = useState<number>();
  const [initialSortBy] = useState<SortingRule<object>[]>(Utilities.LoadSortParametersFromURLQuery("sortField", "sortOrder"));
  const [reloadSearch, setReloadSearch] = useState<boolean>(false);

  const [isActiveMaintenanceModule, setIsActiveMaintenanceModule] = useState<boolean>(false);
  const { availableModules, assistanceTypes } = useAvailableModulesContext();

  useEffect(() => {
    if (availableModules.includes("IsActiveMaintenanceModule")) {
      setIsActiveMaintenanceModule(true);
    }
  }, [availableModules]);

  const downloadDataSheet = async (id: number) => {
    setLoading(true)
    var response = await equipmentService.DownloadDataSheet(id);
    if (response.success === false) {
      Toast.Show("error", response.message);
      setLoading(false);
      return;
    }

    Toast.Show("success", response.message);
    setLoading(false);
  }

  const columns = React.useMemo(
    () => [
      {
        Header: "",
        accessor: " ",
        Cell: (props: any) => {
          return (
            <div style={{ display: "flex" }}>
              {OperationsCell(props.cell.row.original.erpId, "equipment")}
              {isActiveMaintenanceModule && props.cell.row.original.hasDataSheet &&
                <Tooltip title="Ficha Técnica" aria-label="Ficha Técnica" enterNextDelay={500} enterDelay={500}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      alignContent: "center",
                      flexDirection: "column",
                      cursor: "pointer",
                    }}
                    onClick={() => {
                      downloadDataSheet(props.cell.row.original.id);
                    }}
                  >
                    <FcDocument className="icons" />
                  </div>
                </Tooltip>
              }
            </div>
          );
        },
        width: 5,
      },
      {
        Header: <FormattedMessage id="Screens.Equipments.Table.CarPlate" />,
        accessor: "carPlate",
        width: 12,
      },
      {
        Header: <FormattedMessage id="Screens.Equipments.Table.NumSerie" />,
        accessor: "serialNumber",
        width: 15,
      },
      {
        Header: <FormattedMessage id="Screens.Equipments.Table.Brand" />,
        accessor: "brand",
        width: 13,
      },
      {
        Header: <FormattedMessage id="Screens.Equipments.Table.Model" />,
        accessor: "model",
        width: 15,
      },
      {
        Header: <FormattedMessage id="Screens.Equipments.Table.Sku" />,
        accessor: "sku",
        width: 15,
      },
      {
        Header: <FormattedMessage id="Screens.Equipments.Table.ClientCode" />,
        accessor: "client.erpId",
        width: 10,
      },
      {
        Header: <FormattedMessage id="Screens.Equipments.Table.ClientName" />,
        accessor: "client.name",
        width: 30,
      },
    ],
    [isActiveMaintenanceModule]
  );

  const fetchData = async (
    page: number,
    pageSize: number,
    sortBy: SortingRule<object>[] | null
  ) => {

    //Atualizamos na history do browser a current page e pageSize
    var searchUrl: string = [
      `?page=${page}&pageSize=${pageSize}`,
      ...[
        ["carPlate", "string", carPlate],
        ["serialNumber", "string", serialNumber],
        ["brand", "string", brand],
        ["model", "string", model],
        ["sku", "string", sku],

        ["codClient", "string", codClient],
        ["clientName", "string", clientName],
      ].map(([key, type, value]) => value ? `${key}=${value}&` : '')
    ].join('');

    //ordenações
    if (sortBy != null && sortBy.length > 0) {
      searchUrl += `${Utilities.AddParamToQueryIfValue(
        "sortField",
        "string",
        sortBy[0].id
      )}${Utilities.AddParamToQueryIfValue(
        "sortOrder",
        "string",
        sortBy[0].desc === true ? "DESC" : "ASC"
      )}`;
    }

    //Se antes não existirem parâmetros de pesquisa, significa que o user veio para aqui de uma rota, então para não ficar com duas rotas duplicadas, fazemos replace à rota
    if (searchParams.toString() === "") {
      navigate(`/equipments${searchUrl}`, { replace: true });
    } else {
      //Se já existirem parâmetros de pesquisa, não fazemos replace mas push
      navigate(`/equipments${searchUrl}`);
    }

    setLoading(true);

    //Vamos obter os dados
    var response = await equipmentService.GetAll(
      mountSearchParameters(),
      Utilities.MountSortParameters(sortBy),
      page,
      pageSize
    );

    if (response.success !== true) {
      setData([]);
      setPageCount(0);
      setLoading(false);
      setCurrentPageSize(0);
      setCurrentPage(0);
      setCurrentSorting(null);
      Toast.Show("error", response.message);
      return;
    }

    //Atribuímos os dados
    setData(response.items);
    setPageCount(response.totalPages);
    setLoading(false);
    setCurrentPageSize(pageSize);
    setCurrentPage(page);
    setCurrentSorting(sortBy);
    setGoToPage(0);

    //Se a pesquisa tiver retornado múltiplas páginas e a página atual for superior ao nº de páginas que chegaram mandamos o user para a página 1
    if (response.totalPages > 0 && page > response.totalPages) {
      setGoToPage(1);
    }
  };

  //Método para montar os parâmetros de pesquisa
  const mountSearchParameters = (): Parameter[] => {
    var parameters: Parameter[] = [];

    parameters.push(new Parameter("carPlate", carPlate));
    parameters.push(new Parameter("serialNumber", serialNumber));
    parameters.push(new Parameter("brand", brand));
    parameters.push(new Parameter("model", model));
    parameters.push(new Parameter("sku", sku));

    parameters.push(new Parameter("codClient", codClient));
    parameters.push(new Parameter("clientName", clientName));

    return parameters;
  };

  const onEnter = () => {
    fetchData(currentPage ?? 1, currentPageSize ?? 5, currentSorting);
  };
  const onButtonClick = async () => {
    setCurrentPage(1);
    setGoToPage(1);
    fetchData(currentPage ?? 1, currentPageSize ?? 5, currentSorting);
  };
  const onRemoveFiltersClick = async () => {
    setCurrentPage(1);
    setGoToPage(1);
    setCarPlate("");
    setSerialNumber("");
    setBrand("");
    setModel("");
    setSku("");

    setClientName("");
    setCodClient("");
    setReloadSearch(true);
  };

  useEffect(() => {
    if (reloadSearch === true) {
      fetchData(currentPage ?? 1, currentPageSize ?? 5, currentSorting);
      setReloadSearch(false);
    }
    return () => { };
  }, [reloadSearch]);

  return (
    <>
      <Row>
        <InputComponent
          xl={2}
          lg={3}
          md={5}
          sm={12}
          xs={12}
          style={{ width: "100%" }}
          type={"text"}
          onEnter={onEnter}
          value={carPlate}
          label={intl.formatMessage({ id: "Screens.Equipments.Search.CarPlate" })}
          readOnly={false}
          onChange={(t: string) => setCarPlate(t)}
        />

        <InputComponent
          xl={2}
          lg={3}
          md={5}
          sm={12}
          xs={12}
          style={{ width: "100%" }}
          type={"text"}
          onEnter={onEnter}
          value={serialNumber}
          label={intl.formatMessage({ id: "Screens.Equipments.Search.NumSerie" })}
          readOnly={false}
          onChange={(t: string) => setSerialNumber(t)}
        />

        <InputComponent
          xl={2}
          lg={3}
          md={5}
          sm={12}
          xs={12}
          style={{ width: "100%" }}
          type={"text"}
          onEnter={onEnter}
          value={brand}
          label={intl.formatMessage({ id: "Screens.Equipments.Search.Brand" })}
          readOnly={false}
          onChange={(t: string) => setBrand(t)}
        />

        <InputComponent
          xl={2}
          lg={3}
          md={5}
          sm={12}
          xs={12}
          style={{ width: "100%" }}
          type={"text"}
          onEnter={onEnter}
          value={model}
          label={intl.formatMessage({ id: "Screens.Equipments.Search.Model" })}
          readOnly={false}
          onChange={(t: string) => setModel(t)}
        />

        <InputComponent
          xl={2}
          lg={3}
          md={5}
          sm={12}
          xs={12}
          style={{ width: "100%" }}
          type={"text"}
          onEnter={onEnter}
          value={sku}
          label={intl.formatMessage({ id: "Screens.Equipments.Search.Sku" })}
          readOnly={false}
          onChange={(t: string) => setSku(t)}
        />

        <InputComponent
          xl={2}
          lg={3}
          md={5}
          sm={12}
          xs={12}
          style={{ width: "100%" }}
          type={"text"}
          onEnter={onEnter}
          value={codClient}
          label={intl.formatMessage({ id: "Screens.Equipments.Search.ClientCode" })}
          readOnly={false}
          onChange={(t: string) => setCodClient(t)}
        />

        <InputComponent
          xl={2}
          lg={3}
          md={5}
          sm={12}
          xs={12}
          style={{ width: "100%" }}
          type={"text"}
          onEnter={onEnter}
          value={clientName}
          label={intl.formatMessage({ id: "Screens.Equipments.Search.ClientName" })}
          readOnly={false}
          onChange={(t: string) => setClientName(t)}
        />
      </Row>

      <Row>
        <Col
          xl={3}
          lg={3}
          md={3}
          sm={12}
          xs={12}
          style={{ marginTop: "1em", display: "inline-flex", gap: "1rem" }}
        >
          <ButtonComponent
            text={intl.formatMessage({ id: "Common.Buttons.Search" })}
            onClick={onButtonClick}
          />
          <ButtonComponent
            text={intl.formatMessage({ id: "Common.Buttons.RemoveFilters" })}
            onClick={onRemoveFiltersClick}
          />
        </Col>
      </Row>

      <div className="pageComponent" style={{ width: "100%" }}>
        <TableComponent
          goToPage={goToPage}
          loading={loading}
          pageCount={pageCount}
          columns={columns}
          manualPagination={true}
          fetchData={fetchData}
          pageSizes={pageSizes}
          initialPage={page}
          initialPageSize={pageSize}
          initialSortBy={initialSortBy}
          data={data}
          dontOrderFirstCollumn={true}
        />
      </div>
    </>
  );
}
